/* QuizPreviewEditor — professional review step before a quiz goes live.
 * The AI output is ALWAYS a draft: the teacher edits questions, options and
 * correct answers here, then explicitly publishes (never automatic). */
import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, CheckCircle2, Send, XCircle, Pencil } from "lucide-react";

const qText = (q) => q.prompt ?? q.question ?? q.text ?? q.sentence ?? "";

export default function QuizPreviewEditor({ draft, onPublish, onCancel, publishing = false, dark = true }) {
  const [title, setTitle] = useState(draft?.title || "Quiz");
  const [questions, setQuestions] = useState(() => JSON.parse(JSON.stringify(draft?.questions || [])));

  const setQ = (i, patch) => setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const setOpt = (i, oi, val) => setQuestions((qs) => qs.map((q, idx) => {
    if (idx !== i) return q;
    const opts = [...(q.options || [])];
    const old = opts[oi];
    opts[oi] = val;
    const patch = { options: opts };
    if (typeof q.correct_answer === "string" && q.correct_answer === old) patch.correct_answer = val;
    return { ...q, ...patch };
  }));
  const setCorrect = (i, oi) => setQuestions((qs) => qs.map((q, idx) => {
    if (idx !== i) return q;
    if (typeof q.answer_index === "number" || q.options) {
      if (typeof q.correct_answer === "string") return { ...q, correct_answer: (q.options || [])[oi] };
      return { ...q, answer_index: oi };
    }
    return q;
  }));
  const isCorrect = (q, oi) => (typeof q.answer_index === "number" ? q.answer_index === oi : q.correct_answer === (q.options || [])[oi]);
  const removeQ = (i) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));

  const publish = () => {
    if (!questions.length) { toast.error("Keep at least one question"); return; }
    onPublish({ ...draft, title, questions });
  };

  const card = dark ? "bg-white/[0.05] border-white/10" : "bg-white border-neutral-200";
  const text = dark ? "text-white" : "text-[#0A0C10]";
  const sub = dark ? "text-white/50" : "text-neutral-500";
  const inputCls = `w-full bg-transparent border rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#7C3AED] ${dark ? "border-white/15 text-white" : "border-neutral-300 text-[#0A0C10]"}`;

  return (
    <div className={`w-full max-w-3xl mx-auto ${text}`} data-testid="quiz-preview-editor">
      <div className="text-center mb-6">
        <div className={`text-[11px] font-mono uppercase tracking-[0.28em] ${sub}`}>/ review before publishing</div>
        <h2 className="font-display font-bold text-3xl tracking-tighter mt-1.5 inline-flex items-center gap-2"><Pencil size={22} /> Edit your quiz</h2>
        <p className={`text-sm mt-1.5 ${sub}`}>Fix wording, change options, move the correct answer — students only see it after you publish.</p>
      </div>

      <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="quiz-edit-title"
        className={`${inputCls} font-bold text-base mb-4`} placeholder="Quiz title" />

      {/* Full list scrolls with the page — no inner scroll trap that stops
          before the last question. */}
      <div className="space-y-3 pb-2">
        {questions.map((q, i) => (
          <div key={i} className={`border rounded-lg p-3.5 ${card}`} data-testid={`quiz-edit-q${i}`}>
            <div className="flex items-start gap-2">
              <span className="text-[10px] font-mono mt-2 opacity-50 shrink-0">Q{i + 1}</span>
              <textarea
                value={qText(q)}
                onChange={(e) => setQ(i, q.prompt !== undefined ? { prompt: e.target.value } : q.question !== undefined ? { question: e.target.value } : q.sentence !== undefined ? { sentence: e.target.value } : { text: e.target.value })}
                data-testid={`quiz-edit-q${i}-text`}
                rows={2}
                className={`${inputCls} resize-none`}
              />
              <button onClick={() => removeQ(i)} data-testid={`quiz-edit-q${i}-delete`} title="Remove question"
                className="p-1.5 rounded hover:bg-red-500/15 text-red-400 shrink-0 mt-1"><Trash2 size={14} /></button>
            </div>
            {Array.isArray(q.options) && q.options.length > 0 && (
              <div className="mt-2.5 space-y-1.5 pl-6">
                {q.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <button
                      onClick={() => setCorrect(i, oi)}
                      data-testid={`quiz-edit-q${i}-correct-${oi}`}
                      title="Mark as correct answer"
                      className={`w-6 h-6 rounded-full border text-[10px] font-bold flex items-center justify-center shrink-0 transition-colors ${isCorrect(q, oi) ? "bg-emerald-500 border-emerald-500 text-black" : dark ? "border-white/25 text-white/50 hover:border-emerald-400" : "border-neutral-300 text-neutral-400 hover:border-emerald-500"}`}
                    >{String.fromCharCode(65 + oi)}</button>
                    <input value={opt} onChange={(e) => setOpt(i, oi, e.target.value)} data-testid={`quiz-edit-q${i}-opt-${oi}`} className={inputCls} />
                  </div>
                ))}
              </div>
            )}
            {(!q.options || !q.options.length) && (typeof q.answer === "string" || typeof q.correct_answer === "string") && (
              <div className="mt-2 pl-6 flex items-center gap-2">
                <span className={`text-[10px] font-mono uppercase shrink-0 ${sub}`}>Answer</span>
                <input
                  value={q.answer ?? q.correct_answer ?? ""}
                  onChange={(e) => setQ(i, q.answer !== undefined ? { answer: e.target.value } : { correct_answer: e.target.value })}
                  data-testid={`quiz-edit-q${i}-answer`} className={inputCls}
                />
              </div>
            )}
            {Array.isArray(q.answers) && !q.options && (
              <div className="mt-2 pl-6 space-y-1.5">
                {q.answers.map((a, ai) => (
                  <div key={ai} className="flex items-center gap-2">
                    <span className={`text-[10px] font-mono uppercase shrink-0 ${sub}`}>Blank {ai + 1}</span>
                    <input
                      value={a}
                      onChange={(e) => setQ(i, { answers: q.answers.map((x, xi) => (xi === ai ? e.target.value : x)) })}
                      data-testid={`quiz-edit-q${i}-blank-${ai}`} className={inputCls}
                    />
                  </div>
                ))}
              </div>
            )}
            {Array.isArray(q.items) && Array.isArray(q.zones) && (
              <div className="mt-2.5 pl-6 flex flex-wrap gap-1.5">
                {q.items.map((it) => {
                  const zone = q.zones.find((z) => z.id === (q.correct || {})[it.id]);
                  return (
                    <span key={it.id} className={`text-[11px] rounded-full px-2.5 py-1 border ${dark ? "border-white/15 text-white/70" : "border-neutral-200 text-neutral-600"}`}>
                      {it.label} → <b>{zone?.label || "?"}</b>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 mt-5">
        <button onClick={onCancel} data-testid="quiz-preview-cancel"
          className={`inline-flex items-center gap-1.5 text-sm px-4 py-2.5 rounded-md border ${dark ? "border-white/15 text-white/70 hover:border-white/40" : "border-neutral-300 text-neutral-600 hover:border-neutral-500"}`}>
          <XCircle size={14} /> Discard draft
        </button>
        <div className={`text-[11px] font-mono ${sub}`}>{questions.length} question{questions.length === 1 ? "" : "s"}</div>
        <button onClick={publish} disabled={publishing} data-testid="quiz-publish-btn"
          className="inline-flex items-center gap-1.5 text-sm font-bold px-5 py-2.5 rounded-md bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-60">
          {publishing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Approve &amp; publish
        </button>
      </div>
      <div className={`text-center text-[10px] mt-2 inline-flex w-full justify-center items-center gap-1 ${sub}`}>
        <CheckCircle2 size={10} /> Nothing is published until you approve it.
      </div>
    </div>
  );
}
